import { useEffect, useRef, useState } from 'react';
import { Button, DatePicker, Input, Select, Switch, Tag, Tooltip } from 'antd';
import { PlusIcon, TrashIcon, RefreshIcon, PencilIcon, CheckmarkIcon } from '../../assets/icons';
import {
  type SubUI, type TaskUI, type WsUI, PRIORITY_OPTS, dueHint, dueValue, newSub, newTask,
} from './planUi';

export interface CoordinatorOption { value: number; label: string }

interface Props {
  value: WsUI[];
  onChange: (next: WsUI[]) => void;
  coordinators: CoordinatorOption[];
  eventDate?: string | null;
  /** refs the AI just added or changed — shown with a highlight */
  highlightRefs?: Set<string>;
  /** hide the part headers and the part chip bar (used inside the change-set view) */
  flat?: boolean;
  /** allow adding parts (plan mode) */
  allowNewPart?: boolean;
}

const PRIORITY_COLOR: Record<string, string> = { Critical: 'red', High: 'orange', Medium: 'blue', Low: 'default' };

/** Task / sub-task cards grouped by part. Compact by default; a pencil opens the edit form. */
export default function TaskCards({
  value: ws, onChange, coordinators, eventDate, highlightRefs, flat, allowNewPart,
}: Props) {
  const coordName = (id?: number | null) => coordinators.find((c) => c.value === id)?.label;
  const [editing, setEditing] = useState<Set<string>>(() => new Set());
  const [openSubs, setOpenSubs] = useState<Set<string>>(() => new Set());
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const [activePart, setActivePart] = useState<string | null>(ws[0]?.key ?? null);

  const toggle = (set: Set<string>, id: string) => {
    const n = new Set(set);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  };

  // new tasks/sub-tasks added by the team open straight in edit mode
  useEffect(() => {
    const blank = ws.flatMap((w) => w.tasks.flatMap((t) => [
      ...(!t.title.trim() && !editing.has(t._cid) ? [t._cid] : []),
      ...t.subtasks.filter((s) => !s.title.trim() && !editing.has(s._cid)).map((s) => s._cid),
    ]));
    if (blank.length) setEditing((e) => { const n = new Set(e); blank.forEach((b) => n.add(b)); return n; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws]);

  /* ---- immutable updates */
  const updTask = (wi: number, ti: number, patch: Partial<TaskUI>, byUser = true) =>
    onChange(ws.map((w, i) => i !== wi ? w : {
      ...w, tasks: w.tasks.map((t, j) => (j !== ti ? t : { ...t, ...patch, ...(byUser ? { _byUser: true } : {}) })),
    }));
  const updSub = (wi: number, ti: number, si: number, patch: Partial<SubUI>, byUser = true) =>
    onChange(ws.map((w, i) => i !== wi ? w : {
      ...w, tasks: w.tasks.map((t, j) => j !== ti ? t : {
        ...t, _byUser: byUser || t._byUser,
        subtasks: t.subtasks.map((s, k) => (k !== si ? s : { ...s, ...patch, ...(byUser ? { _byUser: true } : {}) })),
      }),
    }));
  const addTask = (wi: number) =>
    onChange(ws.map((w, i) => (i !== wi ? w : { ...w, tasks: [...w.tasks, newTask()] })));
  const addSub = (wi: number, ti: number) => {
    const t = ws[wi].tasks[ti];
    setOpenSubs((o) => new Set(o).add(t._cid));
    updTask(wi, ti, { subtasks: [...t.subtasks, newSub()] });
  };
  const addPart = () =>
    onChange([...ws, { key: `part-${ws.length + 1}`, title: 'New part', tasks: [newTask()] }]);
  const renamePart = (wi: number, title: string) =>
    onChange(ws.map((w, i) => (i !== wi ? w : { ...w, title })));

  const jumpTo = (key: string) => {
    setActivePart(key);
    sectionRefs.current[key]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  /* ---- small pieces */
  const renderMeta = ({ item, isSub }: {
    item: { due_date?: string | null; due_offset_days?: number | null; suggested_assignee_id?: number | null;
      assignee_role_hint?: string | null; assignee_reason?: string | null; needs_confirmation?: boolean; depends_on?: string[]; _byUser?: boolean };
    isSub?: boolean;
  }) => {
    const due = dueValue(item, eventDate);
    const who = coordName(item.suggested_assignee_id);
    return (
      <div className={['flex flex-wrap items-center gap-x-3 gap-y-1 text-ink-secondary', isSub ? 'text-[11px]' : 'text-xs'].join(' ')}>
        <span>
          <span className="text-[#9ca3af]">By </span>
          {due ? <span className="font-medium text-ink">{due.format('D MMM')}</span> : <span>no date</span>}
          {item.due_offset_days != null && !item.due_date && <span> · {dueHint(item)}</span>}
        </span>
        <span>
          <span className="text-[#9ca3af]">Who </span>
          {who ? (
            <Tooltip title={item._byUser && !item.assignee_reason ? 'Chosen by you' : item.assignee_reason || undefined}>
              <span className="cursor-default font-medium text-ink underline decoration-dotted decoration-line underline-offset-2">{who}</span>
            </Tooltip>
          ) : item.assignee_role_hint ? <span className="italic">{item.assignee_role_hint} (suggested)</span> : <span className="italic">not assigned</span>}
        </span>
        {item.needs_confirmation && <span className="rounded bg-[#FFF7ED] px-1.5 py-0.5 text-[11px] font-medium text-[#C2410C]">needs confirmation</span>}
        {(item.depends_on?.length ?? 0) > 0 && (
          <Tooltip title={`Do after: ${item.depends_on!.join(', ')}`}><span className="cursor-default">after {item.depends_on!.length} task(s)</span></Tooltip>
        )}
      </div>
    );
  };

  const renderIconBtn = ({ title, onClick, danger, active, children }: {
    title: string; onClick: () => void; danger?: boolean; active?: boolean; children: React.ReactNode;
  }) => (
    <Tooltip title={title}>
      <button type="button" onClick={onClick}
        className={['flex size-7 shrink-0 items-center justify-center rounded-md transition-colors',
          active ? 'bg-brand text-white hover:bg-brand-dark'
            : danger ? 'text-[#9ca3af] hover:bg-[#FEF2F2] hover:text-[#DC2626]'
              : 'text-[#9ca3af] hover:bg-brand-shade hover:text-brand'].join(' ')}>
        {children}
      </button>
    </Tooltip>
  );

  const renderAssignee = ({ item, onChange: set }: {
    item: { suggested_assignee_id?: number | null; assignee_role_hint?: string | null; assignee_reason?: string | null; _byUser?: boolean };
    onChange: (id: number | null) => void;
  }) => (
    <div className="flex flex-col gap-1">
      <label className="text-[11px] font-medium uppercase tracking-wide text-[#9ca3af]">Who does it</label>
      <Select size="middle" allowClear showSearch optionFilterProp="label"
        placeholder={item.assignee_role_hint ? `Suggested: ${item.assignee_role_hint}` : 'Not assigned yet'}
        value={item.suggested_assignee_id ?? undefined} onChange={(v) => set(v ?? null)} options={coordinators} />
      {item.assignee_reason && !item._byUser && <span className="text-xs text-ink-secondary">Why: {item.assignee_reason}</span>}
    </div>
  );

  const renderDue = ({ item, onChange: set }: {
    item: { due_date?: string | null; due_offset_days?: number | null };
    onChange: (iso: string | null) => void;
  }) => (
    <div className="flex flex-col gap-1">
      <label className="text-[11px] font-medium uppercase tracking-wide text-[#9ca3af]">Do it by</label>
      <DatePicker className="w-full" value={dueValue(item, eventDate)} onChange={(d) => set(d ? d.format('YYYY-MM-DD') : null)} />
      <span className="text-xs text-ink-secondary">{dueHint(item)}</span>
    </div>
  );

  /* ---- sub-task */
  const renderSub = ({ s, wi, ti, si }: { s: SubUI; wi: number; ti: number; si: number }) => {
    const isEditing = editing.has(s._cid);
    return (
      <div className={['rounded-md border px-3 py-2', s._dropped ? 'border-dashed border-line-soft opacity-50' : 'border-line-soft bg-bg/40'].join(' ')}>
        {!isEditing ? (
          <div className="flex items-start gap-2">
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className={['text-sm text-ink', s._dropped ? 'line-through' : ''].join(' ')}>{s.title || <span className="italic text-ink-secondary">untitled</span>}</span>
                {s._byUser && <Tag className="!m-0 !text-[10px]">edited by you</Tag>}
              </div>
              {s.description && <div className="text-xs text-ink-secondary">{s.description}</div>}
              {renderMeta({ item: s, isSub: true })}
            </div>
            {!s._dropped && renderIconBtn({ title: 'Edit', onClick: () => setEditing((e) => toggle(e, s._cid)), children: <PencilIcon className="size-3.5" /> })}
            {renderIconBtn({
              title: s._dropped ? 'Put it back' : 'Remove', danger: !s._dropped,
              onClick: () => updSub(wi, ti, si, { _dropped: !s._dropped }, false),
              children: s._dropped ? <RefreshIcon className="size-3.5" /> : <TrashIcon className="size-3.5" />,
            })}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <Tag color="cyan" className="!m-0">SUB-TASK</Tag>
              <Button size="small" type="primary" icon={<CheckmarkIcon className="size-3" />} onClick={() => setEditing((e) => toggle(e, s._cid))}>Done</Button>
            </div>
            <Input autoFocus placeholder="What has to be done?" value={s.title} onChange={(e) => updSub(wi, ti, si, { title: e.target.value })} />
            <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} placeholder="Details (optional)" value={s.description ?? ''}
              onChange={(e) => updSub(wi, ti, si, { description: e.target.value })} />
            <div className="grid gap-3 sm:grid-cols-2">
              {renderDue({ item: s, onChange: (iso) => updSub(wi, ti, si, { due_date: iso }) })}
              {renderAssignee({ item: s, onChange: (id) => updSub(wi, ti, si, { suggested_assignee_id: id, assignee_reason: null }) })}
            </div>
          </div>
        )}
      </div>
    );
  };

  /* ---- task */
  const renderTask = ({ t, wi, ti }: { t: TaskUI; wi: number; ti: number }) => {
    const hl = !!t.ref && !!highlightRefs?.has(t.ref);
    const isEditing = editing.has(t._cid);
    const subsOpen = openSubs.has(t._cid) || t.subtasks.some((s) => editing.has(s._cid));
    const liveSubs = t.subtasks.filter((s) => !s._dropped).length;
    return (
      <div className={['rounded-lg border bg-white transition-colors',
        t._dropped ? 'border-dashed border-line-soft opacity-50'
          : hl ? 'border-brand/60 shadow-[0_0_0_3px_rgba(0,97,255,0.08)]' : 'border-line-soft hover:border-line'].join(' ')}>
        {!isEditing ? (
          <div className="flex items-start gap-3 px-4 py-3">
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <div className="flex flex-wrap items-center gap-2">
                <span className={['text-[15px] font-semibold leading-6 text-ink', t._dropped ? 'line-through' : ''].join(' ')}>
                  {t.title || <span className="font-normal italic text-ink-secondary">untitled task</span>}
                </span>
                {t.priority && <Tag color={PRIORITY_COLOR[t.priority] ?? 'default'} className="!m-0">{t.priority}</Tag>}
                {hl && <Tag color="processing" className="!m-0">just changed</Tag>}
                {t._byUser && <Tag className="!m-0">edited by you</Tag>}
              </div>
              {t.description && <p className="m-0 text-sm leading-[21px] text-ink-secondary">{t.description}</p>}
              {renderMeta({ item: t })}
              {t.subtasks.length > 0 && !t._dropped && (
                <button type="button" onClick={() => setOpenSubs((o) => toggle(o, t._cid))}
                  className="mt-0.5 w-fit text-xs font-medium text-brand hover:underline">
                  {subsOpen ? 'Hide' : 'Show'} {liveSubs} sub-task{liveSubs === 1 ? '' : 's'}
                </button>
              )}
            </div>
            {!t._dropped && renderIconBtn({ title: 'Edit', onClick: () => setEditing((e) => toggle(e, t._cid)), children: <PencilIcon className="size-4" /> })}
            {renderIconBtn({
              title: t._dropped ? 'Put it back' : 'Remove from the plan', danger: !t._dropped,
              onClick: () => updTask(wi, ti, { _dropped: !t._dropped }, false),
              children: t._dropped ? <RefreshIcon className="size-4" /> : <TrashIcon className="size-4" />,
            })}
          </div>
        ) : (
          <div className="flex flex-col gap-4 px-4 py-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Tag color="blue" className="!m-0">TASK</Tag>
                <Select size="small" className="w-[110px]" value={t.priority ?? 'Medium'} options={PRIORITY_OPTS}
                  onChange={(v) => updTask(wi, ti, { priority: v })} />
                <Tooltip title="Needs the leader's or office's confirmation (money, vehicles, leader's presence)">
                  <span className="flex items-center gap-1.5 text-xs text-ink-secondary">
                    <Switch size="small" checked={!!t.needs_confirmation} onChange={(v) => updTask(wi, ti, { needs_confirmation: v })} />
                    needs confirmation
                  </span>
                </Tooltip>
              </div>
              <Button size="small" type="primary" icon={<CheckmarkIcon className="size-3" />} onClick={() => setEditing((e) => toggle(e, t._cid))}>Done</Button>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[11px] font-medium uppercase tracking-wide text-[#9ca3af]">Task</label>
              <Input autoFocus placeholder="What has to be done?" value={t.title} className="!text-[15px] !font-medium"
                onChange={(e) => updTask(wi, ti, { title: e.target.value })} />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[11px] font-medium uppercase tracking-wide text-[#9ca3af]">Details</label>
              <Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} placeholder="Details (optional)" value={t.description ?? ''}
                onChange={(e) => updTask(wi, ti, { description: e.target.value })} />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {renderDue({ item: t, onChange: (iso) => updTask(wi, ti, { due_date: iso }) })}
              {renderAssignee({ item: t, onChange: (id) => updTask(wi, ti, { suggested_assignee_id: id, assignee_reason: null }) })}
            </div>
          </div>
        )}

        {!t._dropped && (subsOpen || isEditing) && (
          <div className="flex flex-col gap-2 border-t border-line-soft bg-bg/30 px-4 py-3">
            {t.subtasks.map((s, si) => <div key={s._cid}>{renderSub({ s, wi, ti, si })}</div>)}
            <div>
              <Button size="small" type="text" className="!px-1 !text-brand" icon={<PlusIcon className="size-3.5" />}
                onClick={() => addSub(wi, ti)}>Add a sub-task</Button>
            </div>
          </div>
        )}
        {!t._dropped && !subsOpen && !isEditing && t.subtasks.length === 0 && (
          <div className="border-t border-line-soft px-4 py-1.5">
            <Button size="small" type="text" className="!px-1 !text-xs !text-ink-secondary hover:!text-brand" icon={<PlusIcon className="size-3" />}
              onClick={() => addSub(wi, ti)}>Add a sub-task</Button>
          </div>
        )}
      </div>
    );
  };

  /* ---- layout */
  return (
    <div className="flex flex-col gap-6">
      {!flat && ws.length > 1 && (
        <div className="sticky top-0 z-10 -mx-1 flex flex-wrap items-center gap-1.5 bg-white/95 px-1 py-2 backdrop-blur">
          <span className="mr-1 text-[11px] font-medium uppercase tracking-wide text-[#9ca3af]">Parts</span>
          {ws.map((w) => (
            <button key={w.key} type="button" onClick={() => jumpTo(w.key)}
              className={['rounded-full border px-2.5 py-1 text-xs transition-colors',
                activePart === w.key ? 'border-brand bg-brand-shade text-brand' : 'border-line-soft bg-white text-ink-secondary hover:border-brand hover:text-brand'].join(' ')}>
              {w.title} <span className="opacity-60">· {w.tasks.filter((t) => !t._dropped).length}</span>
            </button>
          ))}
        </div>
      )}

      {ws.map((w, wi) => (
        <section key={w.key} ref={(el) => { sectionRefs.current[w.key] = el; }} className="flex scroll-mt-14 flex-col gap-3">
          {!flat && (
            <div className="flex items-center gap-3 border-b border-line-soft pb-2">
              <Input variant="borderless" className="!px-0 !text-base !font-semibold !text-ink" value={w.title}
                onChange={(e) => renamePart(wi, e.target.value)} />
              <span className="shrink-0 text-xs text-ink-secondary">{w.tasks.filter((t) => !t._dropped).length} task(s)</span>
            </div>
          )}
          {w.tasks.map((t, ti) => <div key={t._cid}>{renderTask({ t, wi, ti })}</div>)}
          <div>
            <Button size="small" type="dashed" icon={<PlusIcon className="size-3.5" />} onClick={() => addTask(wi)}>Add a task</Button>
          </div>
        </section>
      ))}
      {allowNewPart && (
        <div><Button type="dashed" icon={<PlusIcon className="size-3.5" />} onClick={addPart}>Add a part</Button></div>
      )}
    </div>
  );
}
